import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './AlpacaPortfolio.css';

const Crons = () => {
  const { token } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [runningCode, setRunningCode] = useState('');
  const [notice, setNotice] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch('/api/cron/tasks', { headers: token ? { 'Authorization': `Bearer ${token}` } : {} });
      const ct = res.headers.get('content-type') || '';
      let data;
      if (ct.includes('application/json')) {
        data = await res.json();
      } else {
        const text = await res.text();
        throw new Error(text.slice(0, 300));
      }
      if (!res.ok) throw new Error(data.message || 'Erreur de chargement');
      setTasks(data.tasks || []);
    } catch (e) {
      setError(e.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]);

  const saveTask = async (task) => {
    try {
      const res = await fetch(`/api/cron/tasks/${encodeURIComponent(task.code)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ cron_expr: task.cron_expr, enabled: !!task.enabled })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Échec de la mise à jour');
      setNotice('Tâche mise à jour');
      await load();
    } catch (e) {
      setError(e.message || 'Erreur');
    }
  };

  const pollTaskStatus = (taskCode, headers) => {
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`/api/cron/task-status/${encodeURIComponent(taskCode)}`, { headers });
        const data = await res.json();
        if (!data.running) {
          clearInterval(interval);
          setRunningCode('');
          setNotice('Analyse IA terminée ✔');
        }
      } catch (e) {
        clearInterval(interval);
        setRunningCode('');
        setNotice('Analyse IA terminée ✔');
      }
    }, 5000);
  };

  const runNow = async (task) => {
    setError('');
    setNotice('');
    setRunningCode(task.code);
    const headers = { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) };
    try {
      const res = await fetch(`/api/cron/run/${encodeURIComponent(task.code)}`, {
        method: 'POST',
        headers
      });
      const ct = res.headers.get('content-type') || '';
      const data = ct.includes('application/json') ? await res.json() : { message: await res.text() };
      if (!res.ok || data.success === false) throw new Error(data.message || 'Échec de l\'exécution');
      const r = data.result || {};
      // Background tasks: poll until done instead of showing result immediately
      if (r.status === 'started' || r.status === 'already_running') {
        setNotice(r.status === 'already_running' ? 'Analyse IA déjà en cours, surveillance...' : 'Analyse IA lancée en arrière-plan (~10 min), surveillance...');
        pollTaskStatus(task.code, headers); // will call setRunningCode('') when done
        return;
      }
      // Synchronous tasks: show result and clear running state
      let details = '';
      if (task.code === 'alpaca_assets_sync') {
        details = ` (MAJ: ${r.inserted_updated ?? '-'}, supprimés: ${r.deleted ?? '-'})`;
      } else if (task.code === 'alpaca_daily_history') {
        const inserted = r.inserted ?? 0;
        details = ` (Barres insérées: ${inserted})`;
        if (inserted === 0) {
          setRunningCode('');
          setNotice(`Exécution terminée ✔${details}. Lancez d'abord la tâche « alpaca_assets_sync » pour remplir la table des actifs.`);
          return;
        }
      } else if (task.code === 'top_gainers_calc') {
        details = ` (Top gainers: ${r.inserted ?? '-'})`;
      } else if (Object.keys(r).length) {
        details = ` (${JSON.stringify(r)})`;
      }
      setNotice(`Exécution terminée ✔${details}`);
    } catch (e) {
      setError(e.message || 'Erreur');
    }
    setRunningCode('');
  };

  return (
    <div className="portfolio-page">
      <div className="portfolio-header">
        <h1 className="portfolio-title">⏱️ Tâches planifiées</h1>
        <p className="portfolio-subtitle">Gérer les fréquences et activer/désactiver les crons</p>
      </div>

      {error && <div className="error-message">{error}</div>}
      {notice && <div className="message success" style={{background:'#d1fae5',border:'1px solid #a7f3d0',color:'#065f46',padding:'1rem',borderRadius:'8px',marginBottom:'1.5rem'}}>{notice}</div>}
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Chargement des tâches...</p>
        </div>
      ) : (
        <div className="section">
          <h2>Liste</h2>
          <div className="section-body">
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Description</th>
                    <th>Cron</th>
                    <th>Activé</th>
                    <th>Dernière exécution</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((t) => (
                    <tr key={t.code}>
                      <td>{t.code}</td>
                      <td>{t.description}</td>
                      <td>
                        <input value={t.cron_expr} onChange={(e)=>{
                          const v=e.target.value; setTasks(prev=>prev.map(x=> x.code===t.code?{...x, cron_expr:v}:x));
                        }} style={{padding:'0.4rem',border:'1px solid #d1d5db',borderRadius:'6px'}} />
                      </td>
                      <td>
                        <input type="checkbox" checked={!!t.enabled} onChange={(e)=>{
                          const v=e.target.checked; setTasks(prev=>prev.map(x=> x.code===t.code?{...x, enabled:v}:x));
                        }} />
                      </td>
                      <td>{t.last_run || '-'}</td>
                      <td>
                        <button className="btn btn-primary" onClick={() => saveTask(t)} style={{marginRight:'0.5rem'}}>💾 Enregistrer</button>
                        <button className="btn btn-secondary" onClick={() => runNow(t)} disabled={runningCode===t.code}>
                          {runningCode===t.code ? '⏳ Exécution...' : '▶ Exécuter maintenant'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Crons;


